import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import {
  useDeclareAccommodationOutcome,
  useIssueGroupVoucher,
} from '@/lib/disruptions/use-disruptions';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// The alternatives ladder's last rungs, as ONE dialog with two explicit paths:
//   · SELF_MANAGED — issue the meal voucher, passenger arranges their own stay.
//   · NO_ACCOMMODATION — "we have nothing to offer", formally recorded.
// Both end the PNR's accommodation story (it leaves the queue for good), so the
// dialog spells out what each records and asks for an explicit confirm. The
// declared outcome is a real cases write with operator + timestamp — not a UI tag.

export type OutcomePath = 'SELF_MANAGED' | 'NO_ACCOMMODATION';

export function OutcomeDialog({
  row,
  disruptionUrn,
  initialPath,
  onClose,
}: {
  row: DisruptionPnrGroupRow;
  disruptionUrn: string;
  initialPath?: OutcomePath;
  onClose: () => void;
}) {
  const { t } = useTranslation('tower');
  const [path, setPath] = useState<OutcomePath>(initialPath ?? 'SELF_MANAGED');
  const [note, setNote] = useState('');
  const voucher = useIssueGroupVoucher({ kind: 'disruption', disruptionUrn });
  const declare = useDeclareAccommodationOutcome({ kind: 'disruption', disruptionUrn });

  const busy = voucher.isPending || declare.isPending;
  const failed = voucher.isError || declare.isError;

  const confirm = async (): Promise<void> => {
    const target = { subCaseUrn: row.subCaseUrn, caseUrn: row.caseUrn, disruptionUrn };
    try {
      if (path === 'SELF_MANAGED') {
        // Voucher first — the outcome should never claim a voucher that failed.
        await voucher.mutateAsync(target);
      }
      await declare.mutateAsync({
        ...target,
        outcome: path,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onClose();
    } catch {
      // Mutation state renders the error inline; the dialog stays open to retry.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('desk.outcome.title')}
            <span className="font-mono">{row.pnrLabel}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <PathOption
            selected={path === 'SELF_MANAGED'}
            onSelect={() => setPath('SELF_MANAGED')}
            title={t('desk.outcome.selfManagedTitle')}
            body={t('desk.outcome.selfManagedBody', { pax: row.paxCount })}
            testId="outcome-self-managed"
          />
          <PathOption
            selected={path === 'NO_ACCOMMODATION'}
            onSelect={() => setPath('NO_ACCOMMODATION')}
            title={t('desk.outcome.noAccommodationTitle')}
            body={t('desk.outcome.noAccommodationBody')}
            tone="destructive"
            testId="outcome-no-accommodation"
          />
          <label className="block text-xs text-muted-foreground">
            {t('desk.outcome.note')}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
              placeholder={
                path === 'NO_ACCOMMODATION'
                  ? t('desk.outcome.notePlaceholderNo')
                  : t('desk.outcome.notePlaceholderSelf')
              }
              data-testid="outcome-note"
            />
          </label>
        </div>

        {failed ? (
          <p className="text-xs text-destructive">
            {t('desk.outcome.failed')}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common:common.cancel')}
          </Button>
          <Button
            variant={path === 'NO_ACCOMMODATION' ? 'destructive' : 'default'}
            onClick={() => void confirm()}
            disabled={busy}
            data-testid="outcome-confirm"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
            {path === 'SELF_MANAGED'
              ? t('desk.outcome.confirmSelf')
              : t('desk.outcome.confirmNo')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PathOption({
  selected,
  onSelect,
  title,
  body,
  tone,
  testId,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  body: string;
  tone?: 'destructive';
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={testId}
      className={cn(
        'w-full rounded-md border p-2.5 text-left transition-colors',
        selected
          ? tone === 'destructive'
            ? 'border-rose-400 bg-rose-50 dark:border-rose-700 dark:bg-rose-950/30'
            : 'border-primary bg-primary/5'
          : 'border-border hover:bg-muted/50',
      )}
    >
      <p className={cn('text-sm font-semibold', tone === 'destructive' && 'text-rose-700 dark:text-rose-300')}>
        {title}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>
    </button>
  );
}
